import type { FC, PropsWithChildren, RefObject } from 'react';
import { useImperativeHandle, useMemo, useRef } from 'react';

import styled from '@emotion/styled';

import { Mixins } from '../../styles';
import { HorizontalScrollDim, Modal, ScrollDim, useModalAnimation } from '../modal';

import type { ModalProps } from '../modal';

export interface FullScreenModalProps extends Pick<ModalProps, 'isOpened' | 'isCached'>, PropsWithChildren {
  onScroll?: (y: number) => void;
  effectType?: 'horizontal' | 'vertical' | 'none';
  scrollRef?: RefObject<{
    scrollTo: (x: number, y: number) => void;
    getScrollPosition: () => [x?: number, y?: number];
  }>;
  zIndex?: number;
}

export type FullScreenModalScrollRef = Required<FullScreenModalProps>['scrollRef']['current'];

/** place-site fullscreen-modal 참고 */
export const FullScreenModal: FC<FullScreenModalProps> = ({
  isOpened,
  children,
  onScroll,
  effectType = 'vertical',
  scrollRef,
  zIndex = 200,
}) => {
  const targetId = '_MODAL_DIM_';
  const { onAnimationEnd, isDetached } = useModalAnimation({ isOpened, targetId });
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  useImperativeHandle(scrollRef, () => ({
    scrollTo: (x: number, y: number) => scrollAreaRef.current?.scrollTo(x, y),
    getScrollPosition: () => [scrollAreaRef.current?.scrollLeft, scrollAreaRef.current?.scrollTop],
  }));

  const Dim = useMemo(() => {
    if (effectType === 'none') {
      return S.NoAnimationDim;
    } else if (effectType === 'vertical') {
      return ScrollDim;
    }
    return HorizontalScrollDim;
  }, [effectType]);

  return (
    <CustomedModal isDetached={isDetached} isOpened={isOpened}>
      <Dim
        className={isOpened ? 'open' : 'close'}
        duration={0.3}
        id={targetId}
        onMouseOut={(e) => {
          e.stopPropagation();
        }}
        onMouseOver={(e) => {
          e.stopPropagation();
        }}
        onAnimationEnd={onAnimationEnd}
        // 기본 willChange 속성이 transform으로 되어 있는데 android lottie 애니메이션 시 깜빡임 이슈가 발생하여 auto로 변경
        style={{ zIndex, willChange: 'auto' }}
      >
        <S.ScrollArea onScroll={(e) => onScroll?.(e.currentTarget.scrollTop)} ref={scrollAreaRef}>
          {children}
        </S.ScrollArea>
      </Dim>
    </CustomedModal>
  );
};

const CustomedModal = styled(Modal)`
  pointer-events: none; /* overlay와 그 자식 요소들은 이벤트를 받지 않음 */
`;
const ScrollArea = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  ${Mixins.HideScrollBar};
  overflow: scroll;
  overscroll-behavior: none;
  pointer-events: auto;
`;

const NoAnimationDim = styled(ScrollDim)`
  // animation을 none으로 하여 제거하면 모달 자체가 닫히지 않는 문제가 발생함
  // 그래서 애니메이션 노출을 0초로 하여 제거된 것처럼 보이게 함
  &.open {
    animation-duration: 0s;
  }

  &.close {
    animation-duration: 0s;
  }
`;

const S = { ScrollArea, NoAnimationDim };
